import React, {useState} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button from 'tbk-components/src/components/Button';
import Icon from 'tbk-components/src/components/Icon';
import Image from 'tbk-components/src/components/Image';
import Map from '../Map';
import { ILocation } from './index';
import SearchBar from "../SearchBar";
import comingSoonMessages from '../../lib/comingSoonMessages';

export interface ILocationMobileNavProps extends IBaseElementProps {
	mobileHeading?: string;
	mapOptions?: google.maps.MapOptions;
	locations?: ILocation[];
	/**
	 * @noUI
	 */
	defaultState?: any;
	/**
	 * @noUI
	 */
	onSearchBarSubmit?: (state:any) => void;
	/**
	 * @noUI
	 */
	includeTypeDropdownOnSearch?: boolean;
}

/**
 * Location
 * @requiresHooks
 */
const LocationMobileNav: React.FC<ILocationMobileNavProps> = ({
	className,
	classNames = [],
	mapOptions = {},
	locations = [],
	mobileHeading,
	defaultState,
	onSearchBarSubmit= (state:any) => {},
	includeTypeDropdownOnSearch = true,
}) => {
	const [selectedLocation, setSelectedLocation] =
		useState<ILocation | null>(null);

	return (
		<div className={className || cn('location', ...classNames)}>
			<div className="location-mobile-nav__controls">
				<Button
					className="offcanvas-back"
					onClick={() => {
						const activeDropdown = document.querySelector(
							'.dropdown-mega-menu.collapse-open .collapse-toggle',
						);
						activeDropdown &&
							(activeDropdown as HTMLButtonElement).click();
					}}
				>
					<Icon name="chevron-left" />
					Back to Categories
				</Button>
				<button
					type="button"
					className="btn-close-offcanvas"
					aria-label="Close Menu"
					onClick={() => {
						const btnClose = document.querySelector(
							'.main-nav__content .btn-close',
						);
						btnClose && (btnClose as HTMLButtonElement).click();
					}}
				>
					<i className="icon icon-close"></i>
				</button>
			</div>
			{mobileHeading && (
				<div className="mobile-location__heading">
					<p>{mobileHeading}</p>
				</div>
			)}
			<SearchBar includeTypeDropdown={includeTypeDropdownOnSearch} onSubmit={onSearchBarSubmit} defaultState={defaultState}/>

			{ ! locations.length
				? <div className="text-center p-5">
					No locations found for your search criteria.  Please try again.
				</div>
				: <div className="location__map">
					<Map
						{...mapOptions}
						markers={locations.reduce(
							(acc, location) => [
								...acc,
								{
									...location.marker,
									onClick: () => setSelectedLocation(location),
								},
							],
							[],
						)}
					/>

					{selectedLocation && (
						<>
							<div className="location__overlay" />
							<div className="location__details">
								<button
									className="location__btn-close"
									onClick={() => setSelectedLocation(null)}
									aria-label="Close"
									type="button"
								>
									<i className="icon icon-close" />
								</button>

								<div className="row">
									<div className="col">
										{selectedLocation.logo && (
											<Image
												{...selectedLocation.logo}
												className="location__logo"
											/>
										)}

										{Array.isArray(
											selectedLocation.buttons,
										) && (
											<div className="location__buttons">
												{selectedLocation.buttons.map(
													(button, btnIndex) => (
														<Button
															key={btnIndex}
															href={button.href}
															label={button.label}
															className={`location__button--${
																button.color ||
																'primary'
															}`}
														/>
													),
												)}
											</div>
										)}
									</div>
									<div className="col">
										{comingSoonMessages(
											selectedLocation.comingSoon,
										).map((message, messageIndex) => (
											<p
												className="location__coming-soon"
												key={messageIndex}
											>
												{message}
											</p>
										))}
										{selectedLocation.address && (
											<address
												className="location__address"
												dangerouslySetInnerHTML={{
													__html: selectedLocation.address.replace(
														/\\n/g,
														'<br />',
													),
												}}
											/>
										)}
										{selectedLocation.phone && (
											<a
												href={`tel:${selectedLocation.phone}`}
												className="location__phone"
											>
												{selectedLocation.phone}
											</a>
										)}
									</div>
									<div className="col">
										{Array.isArray(
											selectedLocation.hours,
										) && (
											<div className="location__hours">
												{selectedLocation.hours.map(
													(hour, hourIndex) => (
														<div key={hourIndex}>
															<strong>{hour.storeName}</strong><br />
															{Array.isArray(
																hour.storeHours,
															) && (
																<div>
																	{hour.storeHours.map(
																		(storeHour, storeHourIndex ) => (
																			<div key={storeHourIndex}>{storeHour}</div>
																		)
																	)}
																</div>
															)}
														</div>
													),
												)}
											</div>
										)}

										{(selectedLocation.linkDetails ||
											selectedLocation.linkDirections) && (
											<div className="location__links">
												{selectedLocation.linkDetails && (
													<a
														href={
															selectedLocation
																.linkDetails.href
														}
														className="location__link"
													>
														{
															selectedLocation
																.linkDetails.label
														}
													</a>
												)}
												{selectedLocation.linkDirections && (
													<a
														href={
															selectedLocation
																.linkDirections.href
														}
														className="location__link"
														target="_blank"
														rel="noreferrer"
													>
														{
															selectedLocation
																.linkDirections
																.label
														}
													</a>
												)}
											</div>
										)}
									</div>
								</div>
								{selectedLocation.banner &&
								selectedLocation.bannerLink && (
									<a
										href={selectedLocation.bannerLink.href}
										title={
											selectedLocation.bannerLink.label
										}
									>
										<Image
											{...selectedLocation.banner}
											className="location__banner"
										/>
									</a>
								)}
							</div>
						</>
					)}
				</div>
			}
		</div>
	);
};

export default LocationMobileNav;
